import React, { useState, useEffect } from 'react';
import MUIDataTable from 'mui-datatables';
import {
  Grid,
  Tooltip,
  IconButton,
  Link,
} from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { Refresh, Launch } from '@material-ui/icons';
import { GET_WRITER_REPORTS_BY_ID } from '../../../../queries/reports';
import { GET_REVIEWERS_WRITERS_TRANSLATORS } from '../../../../queries/user';
import { debounceSearchRender } from '../../../../components/DebouncePlugin';
import {
  WAITING,
  APPROVED,
  REPROVED,
  WORKING,
  DONE,
  PREREVISION,
  REVISION,
  WAITPUBLISHING,
  PUBLISHED,
} from '../../../labels/labelsStatusContent';
import { DataRangeFilter } from '../../../../components/DataRangeFilter/DataRangeFilter';
import { Toast } from '../../../../components/Toast';
import Loading from '../../../../components/Loading';
import PageError from '../../../../components/PageError';
import PageLoading from '../../../../components/PageLoading';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import useStyles from '../../../../themes/tableStyle';
import { setStatusLabel } from '../../../labels';

export default function WriterReportInfo({
  writerId,
  sites,
  actualDateTypeFilter,
  actualSelectedStartDate,
  actualSelectedEndDate,
}) {
  const [writer, setWriter] = useState([]);
  const [titles, setTitles] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(
    actualSelectedStartDate,
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    actualSelectedEndDate,
  );
  const [dateTypeFilter, setDateTypeFilter] = useState(actualDateTypeFilter);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(10);
  const [openLoading, setOpenLoading] = useState(false);
  const [filterSearchText, setFilterSearchText] = useState(null);
  const [filterAndSearchPersist, setFilterAndSearchPersist] = useState({ filterList: [], searchText: null });

  const queryTitles = useQuery(GET_WRITER_REPORTS_BY_ID, {
    variables: {
      sites,
      offset: page * limit,
      limit,
      dateStart: selectedStartDate,
      dateEnd: selectedEndDate,
      status: null,
      writerId,
      searchText: filterSearchText,
    },
    fetchPolicy: 'network-only',
  });

  function apendFilterListToColumns(columns, filterList) {
    filterList.forEach((filter, index) => {
      // eslint-disable-next-line no-param-reassign
      columns[index].options.filterList = filter;
    });
    return columns;
  }

  const { loading, error } = queryTitles;

  const queryUsers = useQuery(GET_REVIEWERS_WRITERS_TRANSLATORS);

  useEffect(() => {
    if (queryTitles.data) {
      const {
        data: { titlesReportByUserId },
      } = queryTitles;
      setTitles(titlesReportByUserId.titles);
      setTotal(titlesReportByUserId.total);
    }
  }, [queryTitles]);

  useEffect(() => {
    if (queryUsers.data) {
      const {
        data: { userListByRole },
      } = queryUsers;
      const writer = userListByRole.find((user) => user.id === writerId);
      setWriter(writer);
    }
  }, [queryUsers, writerId]);

  const updateTitlesTable = (page, rowsPerPage) => {
    setPage(page);
    setLimit(rowsPerPage);
  };

  const refetchTitles = async (showBackdrop = true) => {
    if (showBackdrop) setOpenLoading(true);
    queryTitles.refetch();

    if (showBackdrop) {
      setOpenLoading(false);
      Toast('Titles updated successfully!');
    }
  };

  const renderRefreshList = () => (
    <Tooltip title="Refresh contents">
      <IconButton aria-label="refresh" onClick={() => refetchTitles()}>
        <Refresh />
      </IconButton>
    </Tooltip>
  );

  const options = {
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    responsive: 'standard',
    sort: false,
    filter: false,
    pagination: true,
    serverSide: true,
    count: total,
    page,
    rowsPerPage: limit,
    customSearchRender: debounceSearchRender(700),
    searchText: filterAndSearchPersist.searchText ?? undefined,
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changeRowsPerPage':
          updateTitlesTable(tableState.page,
            tableState.rowsPerPage);
          break;
        case 'changePage':
          updateTitlesTable(tableState.page,
            tableState.rowsPerPage);
          break;
        case 'filterChange':
          updateTitlesTable(tableState.page,
            tableState.rowsPerPage);
          break;
        case 'search':
          setFilterAndSearchPersist({
            ...filterAndSearchPersist,
            searchText: tableState.searchText,
          });
          setFilterSearchText(tableState.searchText);
          break;
        default:
          break;
      }
    },
  };

  const WritersRow = (tableMeta) => {
    const title = tableMeta.rowData[0];
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link>
        {title}
      </Link>
    );
  };

  const classes = useStyles();

  const postUrlLink = (link) => {
    return (
      <Link
        target="_blank"
        rel="noreferrer"
        href={link}
      >
        <Launch style={{ verticalAlign: 'middle' }} />
        Access Link
      </Link>
    );
  };

  const columns = [
    {
      name: 'title',
      label: 'Title',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          if (value && titles) {
            return WritersRow(tableMeta);
          }
          return '';
        },
      },
    },
    {
      name: 'siteDomain',
      label: 'Site',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'niche',
      label: 'Niche',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'tag',
      label: 'Tag',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value?.toUpperCase(),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        display: true,
        filter: false,
        setCellHeaderProps: () => ({ className: classes.statusLabel }),
        filterOptions: {
          logic: (location, filters) => {
            if (location === filters[0]) return true;
            return false;
          },
          names: [
            WAITING,
            APPROVED,
            REPROVED,
            WORKING,
            DONE,
            PREREVISION,
            REVISION,
            WAITPUBLISHING,
            PUBLISHED,
          ],
        },
        customBodyRender: (value) => {
          return setStatusLabel(value);
        },
      },
    },
    {
      name: 'postUrl',
      label: 'Post Url',
      options: {
        display: true,
        filter: false,
        customBodyRender: (value) => {
          if (value) {
            return postUrlLink(value);
          }

          return 'URL Not Available';
        },
      },
    },
  ];

  return (
    <>
      <Loading openLoading={openLoading} />
      <Grid container spacing={0}>
        <Grid item xs={4}>
          <PageTitle title={writer.name} />
        </Grid>
        <Grid item xs={8}>
          <DataRangeFilter
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            setDateTypeFilter={setDateTypeFilter}
            dateTypeFilter={dateTypeFilter}
            setPage={setPage}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          {loading ? (
            <PageLoading />
          ) : error ? (
            <PageError />
          ) : (
            <MUIDataTable
              title={renderRefreshList()}
              data={titles}
              columns={apendFilterListToColumns(columns, filterAndSearchPersist.filterList)}
              options={options}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
